import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import Draggable from 'react-draggable';
import { Stage, Layer, Line, Arrow, Text, Image as KonvaImage } from 'react-konva';
import ThreeDViewer from './ThreeDViewer';

interface AnnotatorProps {
  mediaUrl: string;
  kind: 'image' | 'video' | '3d';
  onClose: () => void;
  onSend: (annotatedBlob: Blob, message: string) => void;
}

type Tool = 'brush' | 'arrow' | 'text';
type DrawElement = {
  id: string;
  tool: Tool;
  color: string;
  points?: number[];
  text?: string;
  x?: number;
  y?: number;
};

export default function AnnotatorModal({ mediaUrl, kind, onClose, onSend }: AnnotatorProps) {
  const [tool, setTool] = useState<Tool>('brush');
  const [color, setColor] = useState<string>('#ff0033');
  const [elements, setElements] = useState<DrawElement[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [localMediaUrl, setLocalMediaUrl] = useState(mediaUrl);
  
  const [mediaImg, setMediaImg] = useState<HTMLImageElement | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const stageRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const intrinsicSize = useRef<{ w: number; h: number } | null>(null);
  
  const [noteText, setNoteText] = useState('');
  const [textInputPos, setTextInputPos] = useState<{x: number, y: number} | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [activeTextInput, setActiveTextInput] = useState('');
  
  // Dimensions for stage
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMinimized, setIsMinimized] = useState(false);

  function calculateDimensions() {
    if (!containerRef.current || !intrinsicSize.current) return;
    const maxWidth = containerRef.current.clientWidth || 800;
    const maxHeight = containerRef.current.clientHeight || 500;
    
    let w = intrinsicSize.current.w;
    let h = intrinsicSize.current.h;
    const ratio = w / h;
    
    if (w > maxWidth) {
      w = maxWidth;
      h = w / ratio;
    }
    if (h > maxHeight) {
      h = maxHeight;
      w = h * ratio;
    }
    
    setDimensions({ width: Math.max(10, w), height: Math.max(10, h) });
  }

  useEffect(() => {
    let active = true;
    const isExternal = mediaUrl.startsWith('http') && !mediaUrl.includes('localhost') && !mediaUrl.includes('127.0.0.1');
    const proxyUrl = isExternal ? `/.netlify/functions/corsproxy?url=${encodeURIComponent(mediaUrl)}` : mediaUrl;

    if (kind === 'image') {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = proxyUrl;
      img.onload = () => {
        if (!active) return;
        setMediaImg(img);
        setLocalMediaUrl(proxyUrl);
        intrinsicSize.current = { w: img.naturalWidth, h: img.naturalHeight };
        calculateDimensions();
      };
      img.onerror = () => {
        if (!active) return;
        console.warn("Failed to load proxied image, trying raw fallback");
        const img3 = new Image();
        img3.src = mediaUrl;
        img3.onload = () => {
           if (!active) return;
           setMediaImg(img3);
           setLocalMediaUrl(mediaUrl);
           intrinsicSize.current = { w: img3.naturalWidth || 800, h: img3.naturalHeight || 600 };
           calculateDimensions();
        };
        img3.onerror = () => {
           if (!active) return;
           setMediaImg(img3);
           setLocalMediaUrl(mediaUrl);
           intrinsicSize.current = { w: 800, h: 600 };
           calculateDimensions();
        };
      };
    } else if (kind === 'video') {
      const v = document.createElement('video');
      v.crossOrigin = 'Anonymous';
      v.src = proxyUrl;
      v.onloadedmetadata = () => {
        if (!active) return;
        setLocalMediaUrl(proxyUrl);
        intrinsicSize.current = { w: v.videoWidth || 800, h: v.videoHeight || 600 };
        calculateDimensions();
      };
      v.onerror = () => {
        if (!active) return;
        console.warn("Failed to load proxied video, trying raw fallback");
        const v2 = document.createElement('video');
        v2.src = mediaUrl;
        v2.onloadedmetadata = () => {
          if (!active) return;
          setLocalMediaUrl(mediaUrl);
          intrinsicSize.current = { w: v2.videoWidth || 800, h: v2.videoHeight || 600 };
          calculateDimensions();
        }
      }
    } else if (kind === '3d') {
      intrinsicSize.current = { w: 800, h: 600 };
      calculateDimensions();
    }
    return () => { active = false; };
  }, [mediaUrl, kind]);

  // Handle Resize dynamically to always keep media and stage beautifully sized
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => {
      calculateDimensions();
    });
    observer.observe(containerRef.current);
    // Initial call once Mounted
    calculateDimensions();
    return () => observer.disconnect();
  }, []);

  // Ensure input gets focus outside of the post-click focus tick
  useEffect(() => {
    if (isTyping && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isTyping]);

  const handleVideoLoaded = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    intrinsicSize.current = { w: v.videoWidth, h: v.videoHeight };
    calculateDimensions();
  };

  const handleMouseDown = (e: any) => {
    if (isTyping) {
      finalizeText();
      return;
    }
    
    const stage = stageRef.current;
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    
    if (tool === 'text') {
      setTextInputPos(pos);
      setIsTyping(true);
      setActiveTextInput('');
      return;
    }
    
    setIsDrawing(true);
    const newEl: DrawElement = {
      id: Date.now().toString(),
      tool,
      color,
      points: [pos.x, pos.y],
    };
    setElements(prev => [...prev, newEl]);
  };

  const handleMouseMove = (e: any) => {
    if (!isDrawing || tool === 'text') return;
    
    const stage = stageRef.current;
    if (!stage) return;
    const point = stage.getPointerPosition();
    if (!point) return;
    
    setElements(prev => {
      if (prev.length === 0) return prev;
      const last = { ...prev[prev.length - 1] };
      if (!last.points) return prev;
      
      if (tool === 'arrow') {
        last.points = [last.points[0], last.points[1], point.x, point.y];
      } else {
        last.points = [...last.points, point.x, point.y];
      }
      return [...prev.slice(0, prev.length - 1), last];
    });
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const finalizeText = () => {
    if (textInputPos && activeTextInput.trim()) {
      const newEl: DrawElement = {
        id: Date.now().toString(),
        tool: 'text',
        color,
        x: textInputPos.x,
        y: textInputPos.y,
        text: activeTextInput,
      };
      setElements(prev => [...prev, newEl]);
    }
    setIsTyping(false);
    setTextInputPos(null);
    setActiveTextInput('');
  };

  const clearAnnotations = () => {
    setElements([]);
  };

  const handleSend = async () => {
    if (!stageRef.current) return;
    
    // We want to draw the video onto a canvas if it's a video
    const baseCanvas = document.createElement('canvas');
    baseCanvas.width = dimensions.width;
    baseCanvas.height = dimensions.height;
    const ctx = baseCanvas.getContext('2d');
    
    try {
      if (ctx) {
        if (kind === 'video' && videoRef.current) {
          ctx.drawImage(videoRef.current, 0, 0, dimensions.width, dimensions.height);
        } else if (kind === 'image' && mediaImg) {
          ctx.drawImage(mediaImg, 0, 0, dimensions.width, dimensions.height);
        } else if (kind === '3d') {
          ctx.fillStyle = '#111';
          ctx.fillRect(0, 0, dimensions.width, dimensions.height);
          ctx.fillStyle = '#fff';
          ctx.font = '20px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('3D Model Reference', dimensions.width/2, dimensions.height/2);
        }
        
        // Draw Konva layer over it
        const konvaCanvas = stageRef.current.toCanvas();
        ctx.drawImage(konvaCanvas, 0, 0);
      }
      
      baseCanvas.toBlob((blob) => {
        if (blob) {
          onSend(blob, noteText);
          onClose();
        }
      }, 'image/png', 0.9);
    } catch (e) {
      console.warn("Tainted canvas export failed. Sending only annotations layer.");
      const konvaCanvas = stageRef.current.toCanvas();
      konvaCanvas.toBlob((blob: Blob) => {
        if (blob) {
          onSend(blob, noteText + "\n[Note: Exported layer only due to CORS]");
          onClose();
        }
      }, 'image/png', 0.9);
    }
  };

  const nodeRef = useRef<HTMLDivElement>(null);
  
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-backdrop-annotator"
      style={{ position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(5, 5, 5, 0.9)', padding: '24px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {/* Header controls outside canvas */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', width: '100%', maxWidth: '1200px', margin: '0 auto 16px auto' }}>
        <h2 className="sc" style={{ fontSize: '14px', margin: 0 }}>Annotator</h2>
        <button className="btn btn--ghost" onClick={onClose}>✕ Close</button>
      </div>

      <div 
        ref={containerRef} 
        style={{ 
          flex: 1, 
          position: 'relative', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          overflow: 'hidden'
        }}
      >
        {dimensions.width > 0 && dimensions.height > 0 && (
          <div style={{ position: 'relative', width: dimensions.width, height: dimensions.height, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
            
            {/* Background Media */}
            {kind === 'video' && (
              <video 
                ref={videoRef} 
                src={localMediaUrl} 
                controls 
                style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', top: 0, left: 0 }} 
                onLoadedMetadata={handleVideoLoaded}
              />
            )}
            {kind === 'image' && mediaImg && (
              <img 
                src={mediaImg.src} 
                style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }} 
              />
            )}
            {kind === '3d' && (
              <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-dim)' }}>
                <ThreeDViewer url={mediaUrl} onAnnotate={onClose} fitContainer={true} />
              </div>
            )}

            {/* Drawing Layer */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }}>
              <Stage 
                width={dimensions.width} 
                height={dimensions.height}
                onPointerDown={handleMouseDown}
                onPointerMove={handleMouseMove}
                onPointerUp={handleMouseUp}
                ref={stageRef}
              >
                <Layer>
                  {elements.map((el) => {
                    if (el.tool === 'brush') {
                      return <Line key={el.id} points={el.points || []} stroke={el.color} strokeWidth={4} tension={0.5} lineCap="round" lineJoin="round" />;
                    }
                    if (el.tool === 'arrow') {
                      return <Arrow key={el.id} points={el.points || []} fill={el.color} stroke={el.color} strokeWidth={4} pointerLength={10} pointerWidth={10} tension={0.5} />;
                    }
                    if (el.tool === 'text') {
                      return <Text key={el.id} x={el.x} y={el.y} text={el.text} fill={el.color} fontSize={20} fontFamily="Inter, sans-serif" fontStyle="bold" />;
                    }
                    return null;
                  })}
                </Layer>
              </Stage>
            </div>

            {/* Typing Overlay */}
            {isTyping && textInputPos && (
              <input
                ref={inputRef}
                type="text"
                value={activeTextInput}
                onChange={(e) => setActiveTextInput(e.target.value)}
                onBlur={finalizeText}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    finalizeText();
                  }
                }}
                style={{
                  position: 'absolute',
                  top: textInputPos.y - 12,
                  left: textInputPos.x,
                  zIndex: 20,
                  background: 'transparent',
                  border: `1px dashed ${color}`,
                  color: color,
                  fontSize: '20px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 'bold',
                  outline: 'none',
                  minWidth: '150px'
                }}
              />
            )}
          </div>
        )}
        
        {/* Floating Draggable Toolbar overlay boundary */}
        <Draggable nodeRef={nodeRef} bounds="parent" handle=".drag-handle" defaultPosition={{ x: 20, y: 20 }}>
          <div ref={nodeRef} className="glass glass--strong" style={{ 
            position: 'absolute', 
            zIndex: 100, 
            padding: isMinimized ? '6px' : '12px', 
            borderRadius: 'var(--radius-sm)', 
            border: '1px solid var(--line-strong)', 
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: isMinimized ? '0px' : '12px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
            width: isMinimized ? '140px' : '260px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="sc drag-handle" style={{ flex: 1, padding: '4px 0', fontSize: '10px', opacity: 0.7, cursor: 'move', userSelect: 'none', WebkitUserSelect: 'none' }}>:: DRAG MENU ::</div>
              <button 
                className="btn btn--icon" 
                onClick={() => setIsMinimized(!isMinimized)} 
                title={isMinimized ? "Expand" : "Minimize"}
                style={{ width: '20px', height: '20px', minHeight: '20px', minWidth: '20px', cursor: 'pointer' }}
              >
                {isMinimized ? '＋' : '－'}
              </button>
            </div>
            
            {!isMinimized && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <button 
                    className={`btn btn--icon ${tool === 'brush' ? 'is-active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setTool('brush'); }}
                    title="Brush"
                    style={{ background: tool === 'brush' ? 'var(--line-strong)' : 'transparent' }}
                  >
                    🖌️
                  </button>
                  <button 
                    className={`btn btn--icon ${tool === 'arrow' ? 'is-active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setTool('arrow'); }}
                    title="Arrow"
                    style={{ background: tool === 'arrow' ? 'var(--line-strong)' : 'transparent' }}
                  >
                    ↗️
                  </button>
                  <button 
                    className={`btn btn--icon ${tool === 'text' ? 'is-active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setTool('text'); }}
                    title="Text"
                    style={{ background: tool === 'text' ? 'var(--line-strong)' : 'transparent' }}
                  >
                    T
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
                  <label style={{ 
                    width: '24px', height: '24px', borderRadius: '50%', background: color, cursor: 'pointer',
                    border: '2px solid var(--fg)', display: 'flex', overflow: 'hidden'
                  }}>
                    <input 
                      type="color" 
                      value={color}
                      onChange={e => setColor(e.target.value)}
                      style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                </div>

                <button className="btn btn--ghost" style={{ fontSize: '10px', justifyContent: 'center', padding: '4px' }} onClick={clearAnnotations}>
                  Clear Canvas
                </button>

                <hr className="divider" style={{ margin: '2px 0' }} />

                <div className="field" style={{ margin: 0 }}>
                  <label style={{ fontSize: '9px' }}>Agent Instructions for this frame</label>
                  <textarea 
                    rows={3} 
                    style={{ fontSize: '11px', padding: '6px', resize: 'none' }} 
                    placeholder="e.g., 'Make the highlighted car red instead of blue...'"
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    onKeyDown={e => e.stopPropagation()}
                    onPointerDown={e => e.stopPropagation()}
                  />
                </div>

                <button className="btn btn--primary" style={{ fontSize: '11px', justifyContent: 'center', minHeight: '32px' }} onClick={handleSend}>
                  Send Annotations
                </button>
              </>
            )}
          </div>
        </Draggable>

      </div>
    </motion.div>,
    document.body
  );
}
